/**
 * @file This file contains the CampaignManager class, responsible for generating
 * and tracking the progression of campaign levels.
 * @module CampaignManager
 */

import { buildGameConfig } from '../lib/utils.js';

/**
 * Manages the sequence of levels in the campaign, including generation,
 * progression, and providing level-specific configurations.
 *
 * @export
 * @class CampaignManager
 * @typedef {CampaignManager}
 */
export class CampaignManager {
    /**
     * @param {object} dependencies - The dependencies for the CampaignManager.
     * @param {object} dependencies.baseConfig - The base game configuration.
     * @param {object} dependencies.biomesConfig - The biomes configuration.
     * @param {object} dependencies.gameOptions - The game options configuration.
     * @param {object} dependencies.masterItemsConfig - The master items configuration.
     */
    constructor(dependencies) {
        /** @private */
        this.baseConfig = dependencies.baseConfig;
        /** @private */
        this.biomesConfig = dependencies.biomesConfig;
        /** @private */
        this.gameOptions = dependencies.gameOptions;
        /** @private */
        this.masterItemsConfig = dependencies.masterItemsConfig;

        /**
         * An array of generated campaign levels.
         * @type {Array<object>}
         */
        this.levels = []; // This would be built from configs
        /**
         * The current level index in the campaign.
         * @type {number}
         */
        this.campaignLevel = 0;
        this.levels = this.generateLevels();
    }

    /**
     * Gets the current campaign level index.
     * @returns {number} The current level index.
     */
    getCurrentLevel() {
        return this.campaignLevel;
    }

    /**
     * Sets the current campaign level index.
     * @param {number} levelIndex - The level index to set.
     */
    setCurrentLevel(levelIndex) {
        this.campaignLevel = levelIndex;
    }

    /**
     * Advances the campaign to the next level.
     */
    advanceLevel() {
        this.campaignLevel++;
    }

    /**
     * Resets the campaign progress to the first level.
     */
    resetCampaign() {
        this.setCurrentLevel(0);
    }

    /**
     * Generates the sequence of campaign levels based on available biomes, grid sizes, and path lengths.
     * @param {string} gameMode - The game mode ('linear' or 'rolling') for which to generate levels.
     * @returns {Array<object>} An array of level configuration objects.
     */
    generateLevels(gameMode) {
        const pathLengthOrder = ['simple', 'default', 'long', 'huge'];

        return pathLengthOrder.flatMap(pathLength =>
            this.gameOptions.gridSizes.flatMap(sizeInfo => {
                const gridSize = sizeInfo.key;
                return Object.keys(this.biomesConfig).map(biomeKey => {
                    const biome = this.biomesConfig[biomeKey];
                    // A level is valid if the biome exists. For linear mode, the path must also exist.
                    // For rolling mode, path doesn't matter for validity, but we check for it to calculate score.
                    const pathData = biome.upgradePathLinear.find(p => p.key === pathLength);
                    if (pathData) {
                        const targetScore = this._calculateTargetScore(gridSize, pathData.path);
                        return {
                            biomeKey,
                            gridSize,
                            pathLength,
                            targetScore
                        };
                    }
                    return null;
                }).filter(level => level !== null); // Filter out invalid combinations
            })
        );
    }

    /**
     * Calculates the target score for a level.
     * @param {number} gridSize - The size of the grid.
     * @param {Array} path - The upgrade path for the level.
     * @returns {number} The calculated target score.
     * @private
     */
    _calculateTargetScore(gridSize, path) {
        // Make score calculation independent of path, as it's not relevant for Rolling Mode.
        // Base the score on grid size and a multiplier for path length concept.
        const pathLengthMultipliers = { 'simple': 0.8, 'default': 1.0, 'long': 1.5, 'huge': 2.0 };
        const pathMultiplier = pathLengthMultipliers[path?.key] || 1.0;
        const gridSizeScore = gridSize * gridSize * 15 * pathMultiplier;
        const pathLengthScore = (path?.length || 10) * 500 * pathMultiplier; // Use default length if path is missing
        return Math.round((gridSizeScore + pathLengthScore) / 100) * 100;
    }

    /**
     * Gets the full game configuration for a specific campaign level.
     * @param {object} settings - The settings for the campaign (gameMode, toggles, etc.).
     * @param {number} levelIndex - The index of the level to retrieve.
     * @returns {object|null} The game configuration object for the specified level, or null if the level does not exist.
     */
    getLevelConfig(settings, levelIndex) {
        if (levelIndex >= this.levels.length) return null; // Campaign complete

        const level = this.levels[levelIndex];

        const gameConfig = buildGameConfig({
            masterItemsConfig: this.masterItemsConfig,
            baseConfig: this.baseConfig,
            biomesConfig: this.biomesConfig,
            biomeKey: level.biomeKey,
            pathLength: level.pathLength,
            gridSize: level.gridSize,
            gameMode: settings.gameMode, // Pass the campaign's game mode
            ...settings // Pass all other campaign-wide settings
        });

        if (gameConfig) { gameConfig.targetScore = level.targetScore; }
        return gameConfig;
    }

}